import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Callout, Checkbox, Label, Modal, Popover, Select, Skeleton } from '@treams/treams-ui';

function sharedCompetencyLevelSet<Level>(
	competencies: { competencyLevelSet: { id: string }; levels: Level[] }[],
): { id: string; levels: Level[] } | undefined {
	if (competencies.length === 0) {
		return undefined;
	}

	const id = competencies[0].competencyLevelSet.id;

	if (!competencies.every((competency) => competency.competencyLevelSet.id === id)) {
		return undefined;
	}

	return { id, levels: competencies[0].levels };
}

export default function JobCompetencyEditorModal(props: {
	jobTitle: graphql.JobTitle;
	selectedCompetencyIDs: string[];
	onDismiss: () => void;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const competencies = graphql.useCompetenciesWithLevelsQuery({
		variables: { isArchived: false, first: 1000 },
	});

	const [attach] = graphql.useAttachJobCompetencyMutation();

	const [competencyIDs, setCompetencyIDs] = useState<Record<string, boolean>>({});
	const [competencyLevel, setCompetencyLevel] = useState<number | undefined>();

	const formik = useFormik({
		initialValues: { competency: { competency: undefined as number | undefined } },
		onSubmit: () => {},
	});

	const availableCompetencies = (competencies.data?.competencies.edges ?? [])
		.map(({ node }) => node)
		.filter((node) => !props.selectedCompetencyIDs.includes(node.id))
		.sort((a, b) => localize(a.title).text.localeCompare(localize(b.title).text));

	const selectedCompetencies = availableCompetencies.filter((node) => competencyIDs[node.id]);
	const bulkLevelSet = sharedCompetencyLevelSet(selectedCompetencies);

	useEffect(() => {
		setCompetencyLevel(undefined);
	}, [bulkLevelSet?.id]);

	const save = async () => {
		const selectedCompetencyIDs = Object.keys(competencyIDs).filter((id) => competencyIDs[id]);
		const level = bulkLevelSet !== undefined && competencyLevel !== undefined ? competencyLevel : 0;

		for (const competencyID of selectedCompetencyIDs) {
			await attach({
				variables: {
					input: {
						jobTitleID: props.jobTitle.id,
						jobLevelID: undefined,
						competencyID,
						competencyLevel: level,
					},
				},
			});
		}

		props.onDismiss();
	};

	return (
		<Popover center visible>
			<Modal>
				<Modal.Header
					title={t('settings.jobCompetencies.create.title')}
					onClose={() => props.onDismiss()}
				/>
				<FormikProvider value={formik}>
					<Modal.Contents>
						<Callout
							color="blue"
							description={t('settings.jobCompetencies.create.levelsWarning')}
							mb={2}
						/>

						{competencies.loading && !competencies.data && (
							<Skeleton.Repeat count={3}>
								<Skeleton.Box height={32} my={2} />
							</Skeleton.Repeat>
						)}

						{availableCompetencies.map((node) => (
							<Checkbox
								key={node.id}
								label={localize(node.title).text}
								checked={competencyIDs[node.id]}
								onClick={() => {
									setCompetencyIDs({ ...competencyIDs, [node.id]: !competencyIDs[node.id] });
								}}
							/>
						))}

						{bulkLevelSet && (
							<Box mt={3}>
								<Label mb={1}>{t('settings.jobCompetencies.create.competencyLevelLabel')}</Label>
								<Select
									search={false}
									clearable
									placeholder={t('common.optional')}
									selectedValue={competencyLevel?.toString()}
									onChange={(value) => {
										setCompetencyLevel(value !== undefined ? parseInt(value, 10) : undefined);
									}}
									options={bulkLevelSet.levels.map((level, idx) => ({
										title: localize(level.level.title).text,
										value: idx.toString(),
									}))}
								/>
							</Box>
						)}
					</Modal.Contents>
					<Modal.FormFooter
						saveTitle={t('settings.jobCompetencies.create.save')}
						onSave={save}
						onCancel={props.onDismiss}
						disabled={!Object.values(competencyIDs).some((value) => value === true)}
					/>
				</FormikProvider>
			</Modal>
		</Popover>
	);
}
